/*@jsxRuntime automatic @jsxImportSource react*/
/*## old
<Image src="/scenarios/simulation.svg" style={{ width: '100%', backgroundColor: "white" }} />

**Legend:**
<span style={{ color: "darkorange" }}><Image src="/scenarios/simulation-legend.svg" style={{ width: '100%', backgroundColor: "darkorange" }} /> waiting</span>
<span style={{ color: "red" }}><Image src="/scenarios/simulation-legend.svg" style={{ width: '100%', backgroundColor: "red" }} /> lockValue</span>
<span style={{ color: "purple" }}><Image src="/scenarios/simulation-legend.svg" style={{ width: '100%', backgroundColor: "purple" }} /> request rate per second</span>*/
/**Fig 1. WebSemaphore's impact on traffic**/
/*The rest of this document presents how WebSemaphore can fit in different integration scenarios.*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "next-mdx-import-source-file";
export const frontmatter = undefined;
import {Image} from "antd";
function _createMdxContent(props) {
  const _components = Object.assign({
    h1: "h1",
    h2: "h2",
    p: "p",
    a: "a",
    em: "em",
    strong: "strong",
    ol: "ol",
    li: "li",
    h3: "h3",
    blockquote: "blockquote"
  }, _provideComponents(), props.components);
  return _jsxs(_Fragment, {
    children: [_jsx(_components.h1, {
      children: "Systems Integration Scenarios"
    }), "\n", _jsx(_components.h2, {
      children: "1. Intro"
    }), "\n", _jsxs(_components.p, {
      children: ["WebSemaphore helps achieve lossless amortization of traffic to comply with requirements such as concurrent request/execution limit or exclusive access to a resource.\nThe concept of amortization should be understandable from the chart below. Please see the ", _jsx(_components.a, {
        href: "/docs/v1/concepts/operating-modes",
        children: "operating modes"
      }), " section\nfor a complete comparison of ", _jsx(_components.em, {
        children: "async"
      }), " and ", _jsx(_components.em, {
        children: "sync"
      }), " operating modes."]
    }), "\n", _jsx(Image, {
      src: "/charts/LinesAsync.svg",
      style: {
        width: '100%'
      }
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Legend:"
      }), "\n", _jsxs("span", {
        style: {
          color: "red"
        },
        children: [_jsx(Image, {
          src: "/scenarios/simulation-legend.svg",
          style: {
            width: '100%',
            backgroundColor: "red"
          }
        }), "lockValue"]
      }), "\n", _jsxs("span", {
        style: {
          color: "darkorange"
        },
        children: [_jsx(Image, {
          src: "/scenarios/simulation-legend.svg",
          style: {
            width: '100%',
            backgroundColor: "darkorange"
          }
        }), " waiting"]
      }), "\n", _jsxs("span", {
        style: {
          color: "purple"
        },
        children: [_jsx(Image, {
          src: "/scenarios/simulation-legend.svg",
          style: {
            width: '100%',
            backgroundColor: "purple"
          }
        }), " request rate per second"]
      })]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Fig 2.1"
      }), " ", _jsx(_components.em, {
        children: "WebSemaphore's impact on traffic in async mode. Note how the lockValue (red) extends far beyond the traffic spikes (purple) consuming the waiting requests (yellow)"
      }), ".\nSee more charts and explanations in the ", _jsx(_components.a, {
        href: "/docs/v1/concepts/operating-modes",
        children: "operating modes"
      }), " section."]
    }), "\n", _jsx(_components.p, {
      children: "In this section we focus on scenarios using the asynchronous access mode."
    }), "\n", "\n", "\n", "\n", _jsx(_components.h2, {
      children: "2. Assumptions"
    }), "\n", _jsx(_components.p, {
      children: "We hope WebSemaphore fits as many use cases as possible, but integrations that have the following traits will more likely benefit from the features:"
    }), "\n", _jsxs(_components.ol, {
      children: ["\n", _jsx(_components.li, {
        children: "The integration is expected to be lossless. In cases when response time is preferred over 100% quality such as media or telephony,\nthis requirement may be redundant for the core application."
      }), "\n", _jsx(_components.li, {
        children: "The order of requests will be maintained. In other words, requests will be processed on a FIFO/FCFS basis."
      }), "\n", _jsxs(_components.li, {
        children: ["The communication is expected to be near-realtime. When similar flows are performed in batch (e.g. scheduled ETL) mode it may be easier to control the number\nof processes performing a task since task invocation is tightly controlled by the scheduling mechanism. Not so in realtime where\nthe integration traffic depends on business activity such as users performing tasks in business applications or inbound requests from external systems.\nFor periods when demand exceeds capacity the delivery becomes ", _jsx(_components.em, {
          children: "eventual"
        }), "."]
      }), "\n", _jsx(_components.li, {
        children: "Eventual delivery/consistency is assumed to be acceptable. During peak times the solution will prefer to not \"fail fast\" but rather deliver eventually."
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: "To summarize the above: the integration requirements are assumed to favor guaranteed delivery over near-realtime delivery over immediate data consistency."
    }), "\n", _jsx(_components.h2, {
      children: "3. Usage Scenarios"
    }), "\n", _jsx("a", {
      name: "usage-scenarios"
    }), "\n", _jsx(_components.p, {
      children: "While the variety of use cases WebSemaphore can support is potentially endless, it boils down to the following high-level scenarios."
    }), "\n", _jsx(_components.h3, {
      children: "3.1 Provider is external to consumer"
    }), "\n", _jsx("a", {
      name: "provider-external-to-cust"
    }), "\n", _jsx(_components.p, {
      children: "This is the most common scenario. The consumer is invoking an API that has a limit on the number of concurrent connections.\nWhen the consumer's traffic is significantly lower than the limit there is no direct benefit in queueing the requests. However if the traffic\nincreases occasionally or regularly, the consumer will need to implement a queueing solution that will need to consider resource allocation and release.\nWebSemaphore reduces this to an acquire and release calls where the queueing and resource allocation is performed internally by WebSemaphore."
    }), "\n", _jsx(_components.p, {
      children: "The following represents the solution using WebSemaphore"
    }), "\n", _jsxs(_components.blockquote, {
      children: ["\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "The Consumer requests a lock from WebSemaphore"
        }), "\n", _jsx(_components.li, {
          children: "WebSemaphore queues the request"
        }), "\n", _jsx(_components.li, {
          children: "When the resource is available, WebSemaphore grants the lock to the client by invoking a callback endpoint implemented by the Consumer"
        }), "\n", _jsx(_components.li, {
          children: "In the process invoked by the callback the Consumer uses the external resource, processes the job."
        }), "\n", _jsx(_components.li, {
          children: "The client releases the semaphore."
        }), "\n"]
      }), "\n"]
    }), "\n", _jsx(Image, {
      src: "/scenarios/scenario1.svg",
      style: {
        width: '100%'
      }
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.em, {
        children: "Fig 2. Provider is external to customer"
      })
    }), "\n", _jsx(_components.h3, {
      children: "3.2 Provider is internal to consumer"
    }), "\n", _jsx("a", {
      name: "provider-internal-to-cuis"
    }), "\n", _jsx(_components.p, {
      children: "This scenario is similar to scenario 1, but involves a system internal to the business, such as a high-cost computation resource,\na physical device or a legacy system. In such scenario WebSemphore will serve as a queueing mechanism and keep access count to prevent\nsimulatenous access to the limited resource.\nSince the limited resource is internal to the business it is easy to wrap the resource API in a WebSemaphore and avoid the callback phase\n(step 4 in scenario 1), instead providing the results to the consumer in an asynchronous fashion.\nNote: If communication of results from the service is required, it will take the shape of another callback. The only real difference from scenario 1 is\nthat the responsibility for releasing the lock is shifted to the scope (=dev team) of the limited resource."
    }), "\n", _jsx(_components.p, {
      children: "The flow for this scenario:"
    }), "\n", _jsxs(_components.blockquote, {
      children: ["\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "The Consumer requests the Provider service encompassing the limited resource masked by WebSemaphore"
        }), "\n", _jsx(_components.li, {
          children: "WebSemaphore queues the request"
        }), "\n", _jsx(_components.li, {
          children: "When the resource is available, WebSemaphore grants the lock and invokes a callback endpoint implemented by the Provider"
        }), "\n", _jsx(_components.li, {
          children: "In the process invoked by the callback the Provider performes the task over the limited resource, processes the job."
        }), "\n", _jsx(_components.li, {
          children: "The Provider communicates the result back to the consumer."
        }), "\n", _jsx(_components.li, {
          children: "The Provider releases the semaphore."
        }), "\n"]
      }), "\n"]
    }), "\n", _jsx(Image, {
      src: "/scenarios/scenario2.svg",
      style: {
        width: '100%'
      }
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.em, {
        children: "Fig 3. Provider is internal to customer"
      })
    }), "\n", _jsx(_components.h3, {
      children: "3.3 Provider implements WebSemaphore"
    }), "\n", _jsx("a", {
      name: "provider-implements-websemaphore"
    }), "\n", _jsx(_components.p, {
      children: "This scenario is similar to scenarios 1 and 2 but caters more to the provider side.\nBy implementing WebSemaphore in front of their API, providers gain a straightforward solution to avoid overwhelming their core system while\nensuring no requests are lost, even during peak times. This approach requires less resource provisioning, resulting in reduced\nundercapacity issues and no loss of business opportunities for providers. On the other hand, consumers benefit\nfrom not having to deal with the implementation intricacies of WebSemaphore as these details are handled by the provider."
    }), "\n", _jsx(_components.p, {
      children: "Note, since this enforces an asynchronous communication pattern, the Provider will need a way to communicate the response\nback to the Consumer, which similarly to the note in scenario 2, means that the essence of this scenario is that the responsibility for managing\nresource allocation and release is shifted to the Provider."
    }), "\n", _jsx(_components.p, {
      children: "The steps for the flow are exactly as for flow 2. The difference is in the split of responsibility/ownership."
    }), "\n", _jsxs(_components.blockquote, {
      children: ["\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "The Consumer requests the Provider service encompassing the limited resource masked by WebSemaphore"
        }), "\n", _jsx(_components.li, {
          children: "WebSemaphore queues the request"
        }), "\n", _jsx(_components.li, {
          children: "When the resource is available, WebSemaphore grants the lock and invokes a callback endpoint implemented by the Provider"
        }), "\n", _jsx(_components.li, {
          children: "In the process invoked by the callback the Provider performes the task over the limited resource, processes the job."
        }), "\n", _jsx(_components.li, {
          children: "The Provider communicates the result back to the consumer."
        }), "\n", _jsx(_components.li, {
          children: "The Provider releases the semaphore."
        }), "\n"]
      }), "\n"]
    }), "\n", _jsx(Image, {
      src: "/scenarios/scenario3.svg",
      style: {
        width: '100%'
      }
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.em, {
        children: "Fig 4. Provider implements WebSemaphore"
      })
    }), "\n", _jsx(_components.h3, {
      children: "3.4 Conclusion"
    }), "\n", _jsx(_components.p, {
      children: "The async WebSemaphore is no silver bullet that can magically increase the capacity of anything. However under\nmatching circumstances it will contribute to more efficient packing of processes in time to maximize resource usage\nwhile staying within the allocated resource limits."
    }), "\n", _jsxs(_components.p, {
      children: ["In the bottom line this means that when planning the provider capacity, using async WebSemaphore it is sufficient to support enough capacity to handle ", _jsx(_components.em, {
        children: "median"
      }), " load\nrather than always plan for the maximum. This holds true both time-globally when no autoscaling is present and time-locally when there is autoscaling in place."]
    })]
  });
}
function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = Object.assign({}, _provideComponents(), props.components);
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, props)
  })) : _createMdxContent(props);
}
export default MDXContent;
